import { AddressAutocomplete, type ResolvedAddress } from '../components/AddressAutocomplete';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, type DeliveryAddressPayload } from '../api/client';
import { Fulfilment, type Address, type DeliveryQuote, type LoyaltyCard, type ServiceAreaResponse } from '../api/types';
import { Alert, Spinner, money } from '../components/common';
import { useAuth } from '../state/AuthContext';
import { useBranch } from '../state/BranchContext';
import { useCart } from '../state/CartContext';

const emptyAddress = {
  recipientName: '',
  phone: '',
  line1: '',
  line2: '',
  suburb: '',
  city: '',
  postalCode: '',
  instructions: '',
};

export function CheckoutPage() {
  const { customer } = useAuth();
  const { lines, subtotal, clear } = useCart();
  const {
    branches,
    selected: selectedBranch,
    onlyOne: onlyOneBranch,
    choose: chooseBranch,
  } = useBranch();

  const [area, setArea] = useState<ServiceAreaResponse | null>(null);
  const [saved, setSaved] = useState<Address[]>([]);
  const [fulfilment, setFulfilment] = useState<number>(Fulfilment.Delivery);
  const [form, setForm] = useState({ ...emptyAddress });
  const [saveToProfile, setSaveToProfile] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Home');
  const [notes, setNotes] = useState('');

  const [loyalty, setLoyalty] = useState<LoyaltyCard | null>(null);
  const [useReward, setUseReward] = useState(false);
  const [quote, setQuote] = useState<DeliveryQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    Promise.all([
      api.serviceArea(),
      api.addresses().catch(() => [] as Address[]),
      api.loyaltyCard().catch(() => null),
    ])
      .then(([areaResponse, addresses, card]) => {
        setArea(areaResponse);
        setSaved(addresses);
        setLoyalty(card);

        const preferred = addresses.find((a) => a.isDefault) ?? addresses[0];

        setForm((current) => ({
          ...current,
          recipientName: customer?.fullName ?? '',
          phone: customer?.phone ?? '',
          ...(preferred
            ? {
                line1: preferred.line1,
                line2: preferred.line2 ?? '',
                suburb: preferred.suburb,
                city: preferred.city,
                postalCode: preferred.postalCode ?? '',
                instructions: preferred.instructions ?? '',
              }
            : {}),
        }));
      })
      .catch((e: Error) => setError(e.message));
  }, [customer]);

  // Re-price whenever the address, the suburb or the basket changes, so the fee
  // on screen is the fee the API will charge. The fee is now per address, not
  // per suburb, so the street line is part of the input.
  useEffect(() => {
    if (fulfilment !== Fulfilment.Delivery || !form.suburb) {
      setQuote(null);
      return;
    }

    let cancelled = false;
    setQuoting(true);

    const fullAddress = [form.line1, form.suburb, form.city]
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ');

    // 600ms rather than 250: this now reaches a geocoder on the server side,
    // and re-pricing on every keystroke of a street name would hammer a free
    // service for answers nobody is reading yet.
    const timer = window.setTimeout(() => {
      api
        .quoteAddress(fullAddress, form.suburb, subtotal)
        .then((result) => {
          if (!cancelled) setQuote(result);
        })
        .catch(() => {
          if (!cancelled) setQuote(null);
        })
        .finally(() => {
          if (!cancelled) setQuoting(false);
        });
    }, 600);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [form.line1, form.suburb, form.city, subtotal, fulfilment]);

  const [addressNote, setAddressNote] = useState<string | null>(null);

  /** Every suburb the shop prices, flattened for matching Google's answer. */
  const knownSuburbs = useMemo(() => area?.suburbs.map((s) => s.suburb) ?? [], [area]);

  /**
   * Fills the form from a Google place. The suburb is only accepted when it
   * matches one the shop actually prices — otherwise the dropdown is left for
   * the customer, because guessing here would quote a fee for the wrong zone.
   */
  function applyResolvedAddress(address: ResolvedAddress, matchedSuburb: string | null) {
    setForm((current) => ({
      ...current,
      line1: address.line1 || current.line1,
      city: address.city || current.city,
      postalCode: address.postalCode || current.postalCode,
      suburb: matchedSuburb ?? current.suburb,
    }));

    setAddressNote(
      matchedSuburb
        ? null
        : address.suburb
          ? `We don't have a delivery price for ${address.suburb} — pick the closest suburb below.`
          : 'Choose your suburb below so we can price the delivery.',
    );
  }

  const suburbsByRegion = useMemo(() => {
    if (!area) return [];

    return area.regions.map((region) => ({
      region,
      suburbs: area.suburbs.filter((s) => s.region === region),
    }));
  }, [area]);

  const rewardAvailable = (loyalty?.availableRewards ?? 0) > 0;

  // Mirrors the server: the dearest qualifying line, one unit, free.
  const freeItemValue = lines
    .filter((line) => line.earnsLoyaltyStamp)
    .reduce((dearest, line) => Math.max(dearest, line.unitPrice), 0);

  const canUseReward = rewardAvailable && freeItemValue > 0;
  const loyaltyDiscount = useReward && canUseReward ? freeItemValue : 0;

  const deliveryFee = fulfilment === Fulfilment.Delivery ? (quote?.deliveryFee ?? 0) : 0;
  const total = Math.max(subtotal - loyaltyDiscount, 0) + deliveryFee;

  const belowMinimum = area ? subtotal < area.minimumOrderValue : false;

  const canPlace =
    lines.length > 0 &&
    !belowMinimum &&
    !placing &&
    (fulfilment === Fulfilment.Collection || (quote?.isServiceable ?? false));

  function update(field: keyof typeof emptyAddress, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function applySaved(address: Address) {
    setForm((current) => ({
      ...current,
      line1: address.line1,
      line2: address.line2 ?? '',
      suburb: address.suburb,
      city: address.city,
      postalCode: address.postalCode ?? '',
      instructions: address.instructions ?? '',
    }));
    setSaveToProfile(false);
  }

  async function placeOrder() {
    setError(null);
    setPlacing(true);

    const deliveryAddress: DeliveryAddressPayload | null =
      fulfilment === Fulfilment.Delivery
        ? {
            recipientName: form.recipientName.trim(),
            phone: form.phone.trim(),
            line1: form.line1.trim(),
            line2: form.line2.trim() || null,
            suburb: form.suburb,
            city: form.city.trim(),
            postalCode: form.postalCode.trim() || null,
            instructions: form.instructions.trim() || null,
            saveToProfile,
            saveLabel: saveToProfile ? saveLabel.trim() || 'Home' : null,
          }
        : null;

    try {
      const response = await api.placeOrder({
        branchId: selectedBranch?.id ?? null,
        fulfilment,
        items: lines.map((line) => ({
          productVariantId: line.variantId,
          quantity: line.quantity,
          addOnIds: line.addOns.map((a) => a.id),
          choiceIds: line.choices.map((c) => c.choiceId),
          notes: line.notes,
        })),
        deliveryAddress,
        notes: notes.trim() || null,
        useLoyaltyReward: useReward && canUseReward,
      });

      // The cart is cleared before leaving for Paystack; the order itself is
      // already saved and is recoverable from "My orders" either way.
      clear();
      sessionStorage.setItem('es.pendingOrderId', response.order.id);

      window.location.href = response.authorizationUrl;
    } catch (e) {
      const message =
        e instanceof ApiError
          ? [e.message, ...Object.values(e.fieldErrors ?? {}).flat()].join(' ')
          : (e as Error).message;

      setError(message);
      setPlacing(false);
    }
  }

  if (lines.length === 0) {
    return (
      <Alert tone="info">
        Your cart is empty. <Link to="/">Back to the menu</Link>
      </Alert>
    );
  }

  if (!area) {
    return <Spinner label="Loading delivery areas…" />;
  }

  return (
    <section className="page checkout">
      {/* Shown before the customer commits, not after they have paid. Ordering
          is still allowed — the shop takes it and makes it at opening — but
          nobody should learn that from the confirmation email. */}
      {area?.trading && !area.trading.isOpen && (
        <Alert tone="info">
          <strong>We&apos;re closed right now.</strong> {area.trading.message} You can still order
          and pay — yours will be first in the queue.
          <br />
          <span className="muted small">{area.trading.hours}</span>
        </Alert>
      )}
      <h1>Checkout</h1>

      {error && <Alert>{error}</Alert>}

      <div className="checkout__grid">
        <div className="checkout__main">
          {/* Hidden while there is only one shop — a choice of one is not a
              choice. It appears by itself when a second branch opens. */}
          {!onlyOneBranch && (
            <fieldset className="panel">
              <legend>Which shop?</legend>
              <div className="chips">
                {branches.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className={selectedBranch?.id === b.id ? 'chip is-active' : 'chip'}
                    onClick={() => chooseBranch(b.id)}
                    aria-pressed={selectedBranch?.id === b.id}
                  >
                    <span>{b.name}</span>
                    <strong>{b.isOpenNow ? 'Open now' : 'Shut now'}</strong>
                  </button>
                ))}
              </div>
              {selectedBranch && (
                <p className="muted small">
                  {selectedBranch.addressLine} · {selectedBranch.hoursSummary}
                  {!selectedBranch.offersDelivery && ' · collection only'}
                </p>
              )}
            </fieldset>
          )}

          <fieldset className="panel">
            <legend>How would you like it?</legend>
            <div className="chips">
              {/* Disabled for a collection-only shop. The API refuses it too,
                  with the shop's name in the message — this just stops somebody
                  filling in an address first and being told afterwards. */}
              <button
                type="button"
                className={fulfilment === Fulfilment.Delivery ? 'chip is-active' : 'chip'}
                onClick={() => setFulfilment(Fulfilment.Delivery)}
                aria-pressed={fulfilment === Fulfilment.Delivery}
                disabled={selectedBranch ? !selectedBranch.offersDelivery : false}
              >
                <span>Delivery</span>
                <strong>Randburg &amp; Sandton</strong>
              </button>
              <button
                type="button"
                className={fulfilment === Fulfilment.Collection ? 'chip is-active' : 'chip'}
                onClick={() => setFulfilment(Fulfilment.Collection)}
                aria-pressed={fulfilment === Fulfilment.Collection}
              >
                <span>Collection</span>
                <strong>Free</strong>
              </button>
            </div>
          </fieldset>

          {fulfilment === Fulfilment.Delivery ? (
            <fieldset className="panel">
              <legend>Delivery address</legend>

              {saved.length > 0 && (
                <div className="saved">
                  <span className="muted small">Saved addresses:</span>
                  {saved.map((address) => (
                    <button
                      key={address.id}
                      type="button"
                      className="chip chip--sm"
                      onClick={() => applySaved(address)}
                    >
                      <span>{address.label}</span>
                      <strong>{address.suburb}</strong>
                    </button>
                  ))}
                </div>
              )}

              <div className="grid2">
                <label className="field">
                  <span>Recipient name</span>
                  <input value={form.recipientName} onChange={(e) => update('recipientName', e.target.value)} />
                </label>
                <label className="field">
                  <span>Mobile number</span>
                  <input
                    value={form.phone}
                    inputMode="tel"
                    placeholder="0729820176"
                    onChange={(e) => update('phone', e.target.value)}
                  />
                </label>
              </div>

              <label className="field">
                <span>Street address</span>
                <AddressAutocomplete
                  value={form.line1}
                  apiKey={area?.mapsApiKey ?? null}
                  knownSuburbs={knownSuburbs}
                  onTextChange={(v) => update('line1', v)}
                  onResolved={applyResolvedAddress}
                />
                {addressNote && <span className="muted small">{addressNote}</span>}
              </label>

              <label className="field">
                <span>Complex / unit (optional)</span>
                <input value={form.line2} onChange={(e) => update('line2', e.target.value)} />
              </label>

              <div className="grid2">
                <label className="field">
                  <span>Suburb</span>
                  <select value={form.suburb} onChange={(e) => update('suburb', e.target.value)}>
                    <option value="">Choose your suburb…</option>
                    {suburbsByRegion.map((group) => (
                      <optgroup key={group.region} label={group.region}>
                        {group.suburbs.map((s) => (
                          <option key={s.id} value={s.suburb}>
                            {s.suburb}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>City</span>
                  <input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Johannesburg" />
                </label>
              </div>

              <label className="field">
                <span>Delivery instructions (optional)</span>
                <input
                  value={form.instructions}
                  placeholder="Gate code, reception, which entrance…"
                  onChange={(e) => update('instructions', e.target.value)}
                />
              </label>

              <label className="check">
                <input
                  type="checkbox"
                  checked={saveToProfile}
                  onChange={(e) => setSaveToProfile(e.target.checked)}
                />
                <span>Save this address for next time</span>
              </label>

              {saveToProfile && (
                <label className="field">
                  <span>Save as</span>
                  <input value={saveLabel} onChange={(e) => setSaveLabel(e.target.value)} maxLength={40} />
                </label>
              )}

              <p className="muted small">
                We deliver to {area.suburbs.length} suburbs across {area.regions.join(' and ')}. Not on
                the list? Give us a call on <a href="tel:+27729820176">072 982 0176</a>.
              </p>
            </fieldset>
          ) : (
            <div className="panel">
              <h2 className="panel__title">Collect from</h2>
              <p>Randburg Square Food Court, Pretoria Avenue, Ferndale, Randburg</p>
              <p className="muted small">Mon–Sat 08:00–19:00 · Sun 09:00–17:00</p>
            </div>
          )}

          <label className="field panel">
            <span>Anything else we should know? (optional)</span>
            <textarea rows={3} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
        </div>

        <aside className="checkout__side">
          <div className="summary summary--sticky">
            <h2>Order summary</h2>

            <ul className="summary__lines">
              {lines.map((line) => (
                <li key={line.key}>
                  <span>
                    {line.quantity} × {line.productName}{' '}
                    <span className="muted">({line.variantLabel})</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="summary__row">
              <span>Subtotal</span>
              <strong>{money(subtotal)}</strong>
            </div>

            {rewardAvailable && (
              <div className="reward">
                <label className="check">
                  <input
                    type="checkbox"
                    checked={useReward && canUseReward}
                    disabled={!canUseReward}
                    onChange={(e) => setUseReward(e.target.checked)}
                  />
                  <span>
                    Use my free item
                    {canUseReward
                      ? ` — saves ${money(freeItemValue)}`
                      : ' (add a smoothie, juice, shake or bowl)'}
                  </span>
                </label>
              </div>
            )}

            {loyaltyDiscount > 0 && (
              <div className="summary__row">
                <span>Free item</span>
                <strong className="hint-good">−{money(loyaltyDiscount)}</strong>
              </div>
            )}

            {fulfilment === Fulfilment.Delivery && (
              <div className="summary__row">
                <span>Delivery{quoting ? ' …' : ''}</span>
                <strong>
                  {!form.suburb
                    ? '—'
                    : quote?.isServiceable
                      ? quote.isFreeDelivery
                        ? 'FREE'
                        : money(quote.deliveryFee)
                      : '—'}
                </strong>
              </div>
            )}

            <div className="summary__row summary__row--total">
              <span>Total</span>
              <strong>{money(total)}</strong>
            </div>

            {/* The saving is the menu, not a discount on this order: the same
                drink costs the same here as at the counter, because the prices
                are not marked up to cover app commission. Quoting a rand
                figure off the delivery fee suggested the opposite. */}
            {quote?.isServiceable && !quote.isFreeDelivery && (
              <p className="saving">
                Same prices as in store — no app mark-up. Delivery in about{' '}
                {quote.estimatedMinutes} minutes.
              </p>
            )}

            {quote?.isServiceable && quote.isFreeDelivery && (
              <p className="saving">
                Free delivery on this order — you save {money(quote.directOrderDiscount)}.
              </p>
            )}

            {quote && !quote.isServiceable && <Alert>{quote.unavailableReason}</Alert>}

            {belowMinimum && (
              <Alert>
                The minimum order is {money(area.minimumOrderValue)}. Add {money(area.minimumOrderValue - subtotal)}{' '}
                more to check out.
              </Alert>
            )}

            {/* Only shown when the shop has a free-delivery offer running.
                The threshold is zero by policy, so this stays hidden — but the
                guard is explicit rather than relying on `subtotal < 0`. */}
            {area.freeDeliveryThreshold > 0 &&
              !quote?.isFreeDelivery &&
              fulfilment === Fulfilment.Delivery &&
              subtotal < area.freeDeliveryThreshold && (
                <p className="muted small">
                  Spend {money(area.freeDeliveryThreshold - subtotal)} more for free delivery.
                </p>
              )}

            <button
              type="button"
              className="btn btn--primary btn--block"
              disabled={!canPlace}
              onClick={placeOrder}
            >
              {placing ? 'Taking you to payment…' : `Pay ${money(total)}`}
            </button>

            <p className="muted small center">
              Payment is handled securely by Paystack. Card, EFT and SnapScan accepted.
            </p>
          </div>
        </aside>
      </div>
    </section>
  );
}
